import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import i18n from "../../i18n";

const BRZCase = ({ imageName = "brz.jpg" }) => {
  const [langTick, setLangTick] = useState(0);

  useEffect(() => {
    const onLang = () => setLangTick((x) => x + 1);
    i18n.on("languageChanged", onLang);
    return () => i18n.off("languageChanged", onLang);
  }, []);

  const tr = useMemo(() => {
    void langTick;

    return {
      back: i18n.t("brzCase.back"),

      orgTitle: i18n.t("brzCase.org.title"),
      orgDesc: i18n.t("brzCase.org.description"),

      durationLabel: i18n.t("brzCase.labels.duration"),
      toolsLabel: i18n.t("brzCase.labels.tools"),

      overallDuration: i18n.t("brzCase.org.duration"),

      intro: {
        title: i18n.t("brzCase.intro.title"),
        text: i18n.t("brzCase.intro.text"),
      },

      finance: {
        title: i18n.t("brzCase.finance.title"),
        block: {
          duration: i18n.t("brzCase.finance.block.duration"),
          title: i18n.t("brzCase.finance.block.title"),
          text: i18n.t("brzCase.finance.block.text"),
          bullets: [
            i18n.t("brzCase.finance.block.bullets.0"),
            i18n.t("brzCase.finance.block.bullets.1"),
            i18n.t("brzCase.finance.block.bullets.2"),
            i18n.t("brzCase.finance.block.bullets.3"),
            i18n.t("brzCase.finance.block.bullets.4"),
            i18n.t("brzCase.finance.block.bullets.5"),
            i18n.t("brzCase.finance.block.bullets.6"),
          ],
        },
      },

      justice: {
        title: i18n.t("brzCase.justice.title"),

        prison: {
          duration: i18n.t("brzCase.justice.prison.duration"),
          title: i18n.t("brzCase.justice.prison.title"),
          text: i18n.t("brzCase.justice.prison.text"),
          bullets: [
            i18n.t("brzCase.justice.prison.bullets.0"),
            i18n.t("brzCase.justice.prison.bullets.1"),
            i18n.t("brzCase.justice.prison.bullets.2"),
          ],
        },

        court: {
          duration: i18n.t("brzCase.justice.court.duration"),
          title: i18n.t("brzCase.justice.court.title"),
          text: i18n.t("brzCase.justice.court.text"),
          bullets: [
            i18n.t("brzCase.justice.court.bullets.0"),
            i18n.t("brzCase.justice.court.bullets.1"),
            i18n.t("brzCase.justice.court.bullets.2"),
          ],
        },

        legacy: {
          duration: i18n.t("brzCase.justice.legacy.duration"),
          title: i18n.t("brzCase.justice.legacy.title"),
          text: i18n.t("brzCase.justice.legacy.text"),
          bullets: [
            i18n.t("brzCase.justice.legacy.bullets.0"),
            i18n.t("brzCase.justice.legacy.bullets.1"),
          ],
        },
      },
    };
  }, [langTick]);

  const tools = [
    "Java",
    "Spring",
    "Oracle",
    "Tomcat",
    "Jenkins",
    "SonarQube",
    "Windows",
    "Linux",
    "OpenText",
  ];

  const toolMap = {
    Java: "java.png",
    Spring: "spring.png",
    Oracle: "oracle.png",
    Tomcat: "tomcat.png",
    Jenkins: "jenkins.png",
    SonarQube: "sonarqube.png",
    Windows: "microsoft.png",
    Linux: "linux.png",
    OpenText: "opentext.png",
  };

  const handleBack = () => {
    window.history.back();
  };

  const imageUrl = `${process.env.PUBLIC_URL}/images/${imageName}`;

  return (
    <div>
      {/* Header */}
      <div
        className="relative h-64 w-full max-w-4xl bg-contain bg-no-repeat bg-[right_top] flex items-start justify-start"
        style={{ backgroundImage: `url(${imageUrl})` }}
      >
        <div className="max-w-screen-xl w-full">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-700 text-base font-medium py-2 px-4 bg-white/70 hover:bg-white rounded-full m-8"
            type="button"
          >
            <ArrowLeft className="w-5 h-5" />
            {tr.back}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col md:flex-row gap-8 p-4 md:p-8">
        <div className="w-full md:w-[320px]">
          <div className="p-4 flex flex-col gap-4">
            <img
              src={`${process.env.PUBLIC_URL}/logos/brz.png`}
              alt="BRZ Logo"
              className="h-14 object-contain self-start"
            />

            <div>
              <h2 className="text-lg font-semibold">{tr.orgTitle}</h2>
              <p className="text-sm text-gray-600">{tr.orgDesc}</p>
            </div>

            <div>
              <div className="text-sm text-gray-400">{tr.durationLabel}</div>
              <div className="text-sm font-medium">{tr.overallDuration}</div>
            </div>

            <div className="mt-6">
              <div className="text-sm text-gray-400 mb-2">{tr.toolsLabel}</div>
              <div className="flex flex-col gap-2">
                {tools.map((tool) => {
                  const iconFile = `${process.env.PUBLIC_URL}/tools/${
                    toolMap[tool] || `${tool.toLowerCase()}.png`
                  }`;

                  return (
                    <div
                      key={tool}
                      className="flex items-center gap-3 px-5 py-2 bg-white rounded-full shadow text-sm font-medium w-[160px]"
                    >
                      <img
                        src={iconFile}
                        alt={`${tool} icon`}
                        className="w-7 h-7 object-contain"
                      />
                      {tool}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1">
          <div className="px-2 sm:px-4 md:px-0">
            <div className="bg-white text-black font-sans px-0 md:px-6 py-8 space-y-10 rounded-xl">
              <div className="px-4 md:px-0 space-y-10">
                <section className="space-y-4">
                  <h2 className="text-xl font-semibold text-gray-800">
                    {tr.intro.title}
                  </h2>
                  <p>{tr.intro.text}</p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {tr.finance.title}
                  </h2>
                </section>
              </div>

              <section className="bg-gray-100 p-6 rounded-xl space-y-4">
                <p className="text-sm text-gray-500 font-medium">
                  {tr.durationLabel} {tr.finance.block.duration}
                </p>
                <h3 className="text-lg font-semibold">{tr.finance.block.title}</h3>
                <p>{tr.finance.block.text}</p>
                <ul className="list-disc list-inside text-gray-800">
                  {tr.finance.block.bullets.map((b, idx) => (
                    <li key={idx}>{b}</li>
                  ))}
                </ul>
              </section>

              <div className="px-4 md:px-0">
                <section>
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {tr.justice.title}
                  </h2>
                </section>
              </div>

              <section className="bg-gray-100 p-6 rounded-xl space-y-4">
                <p className="text-sm text-gray-500 font-medium">
                  {tr.durationLabel} {tr.justice.prison.duration}
                </p>
                <h3 className="text-lg font-semibold">{tr.justice.prison.title}</h3>
                <p>{tr.justice.prison.text}</p>
                <ul className="list-disc list-inside text-gray-800">
                  {tr.justice.prison.bullets.map((b, idx) => (
                    <li key={idx}>{b}</li>
                  ))}
                </ul>
              </section>

              <section className="bg-gray-100 p-6 rounded-xl space-y-4">
                <p className="text-sm text-gray-500 font-medium">
                  {tr.durationLabel} {tr.justice.court.duration}
                </p>
                <h3 className="text-lg font-semibold">{tr.justice.court.title}</h3>
                <p>{tr.justice.court.text}</p>
                <ul className="list-disc list-inside text-gray-800">
                  {tr.justice.court.bullets.map((b, idx) => (
                    <li key={idx}>{b}</li>
                  ))}
                </ul>
              </section>

              <section className="bg-gray-100 p-6 rounded-xl space-y-4">
                <p className="text-sm text-gray-500 font-medium">
                  {tr.durationLabel} {tr.justice.legacy.duration}
                </p>
                <h3 className="text-lg font-semibold">{tr.justice.legacy.title}</h3>
                <p>{tr.justice.legacy.text}</p>
                <ul className="list-disc list-inside text-gray-800">
                  {tr.justice.legacy.bullets.map((b, idx) => (
                    <li key={idx}>{b}</li>
                  ))}
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BRZCase;
